import { useEffect, useRef, useState } from 'react';
import { can } from '../lib/permissions';
import type { Field, Task } from '../lib/types';
import { useStore } from '../store/useStore';

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

/**
 * A card on the board carries the title and nothing else to read — the rest of the
 * document is one click away. What it does carry is the three things worth doing
 * without opening it: drag it somewhere, take it, finish it.
 */
export function TaskCard({
  task,
  field,
  compact,
  unsynced,
  isDragging,
  onOpen,
  onDragStart,
  onDragEnd,
  onDragOver,
}: {
  task: Task;
  field: Field | undefined;
  /** The owner's choice of card size, from their layout. */
  compact: boolean;
  unsynced: boolean;
  isDragging: boolean;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (event: React.DragEvent) => void;
}) {
  const user = useStore((s) => s.user);
  const access = useStore((s) => s.access);
  const members = useStore((s) => s.members);
  const toggleDone = useStore((s) => s.toggleDone);
  const toggleClaim = useStore((s) => s.toggleClaim);
  const updateTask = useStore((s) => s.updateTask);

  // Native drag needs `draggable` on the card, but only the grip should start one:
  // anywhere else and selecting the title or pressing a button turns into a drag.
  const [armed, setArmed] = useState(false);
  const [picking, setPicking] = useState(false);

  // Taking a task and handing it to somebody else are two permissions, and the
  // same button does both — so which one it does depends on what you may do.
  const mayClaim = can(access, 'task.claim');
  const mayAssign = can(access, 'task.assign');

  const assignee = task.assigneeId ? members.find((m) => m.id === task.assigneeId) : undefined;
  const isMine = task.assigneeId !== null && task.assigneeId === user?.id;
  const done = task.status === 'done';
  const steps = task.checklist ?? [];
  const stepsDone = steps.filter((item) => item.done).length;

  return (
    <article
      draggable={armed && mayClaim}
      onDragStart={(event) => {
        // Firefox refuses to start a drag without payload on the transfer.
        event.dataTransfer.setData('text/plain', task.id);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={() => {
        setArmed(false);
        onDragEnd();
      }}
      onDragOver={onDragOver}
      className={`card group relative flex items-start gap-1 pr-1.5 pl-3 transition-[border-color,background-color,opacity] duration-150 hover:border-line-strong hover:bg-elevated ${
        compact ? 'py-1' : 'py-2'
      } ${isDragging ? 'opacity-40' : ''}`}
    >
      {field && (
        <span
          aria-hidden="true"
          className="absolute inset-y-2 left-1 w-0.5 rounded-full"
          style={{ backgroundColor: field.color }}
          title={field.name}
        />
      )}

      <span
        aria-hidden="true"
        title="Drag to move"
        hidden={!mayClaim}
        onPointerDown={() => setArmed(true)}
        onPointerUp={() => setArmed(false)}
        onPointerCancel={() => setArmed(false)}
        className="drag-grip mt-0.5 shrink-0"
      >
        <svg viewBox="0 0 10 16" className="size-3.5" fill="currentColor">
          <circle cx="3.5" cy="4" r="1.1" />
          <circle cx="6.5" cy="4" r="1.1" />
          <circle cx="3.5" cy="8" r="1.1" />
          <circle cx="6.5" cy="8" r="1.1" />
          <circle cx="3.5" cy="12" r="1.1" />
          <circle cx="6.5" cy="12" r="1.1" />
        </svg>
      </span>

      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 cursor-pointer px-0.5 py-0.5 text-left"
      >
        <span
          className={`block text-sm leading-snug ${
            done ? 'text-ink-soft line-through decoration-ink-faint' : 'text-ink'
          }`}
        >
          {task.title}
        </span>

        {(steps.length > 0 || task.description) && (
          <span className="mt-1 flex items-center gap-2 text-[11px] text-ink-faint">
            {steps.length > 0 && (
              <span>
                {stepsDone}/{steps.length} steps
              </span>
            )}
            {task.description && <span title="Has a description">Notes</span>}
          </span>
        )}
      </button>

      {unsynced && (
        <span
          title="Saved on this device, not yet uploaded"
          className="mt-2 size-1.5 shrink-0 rounded-full bg-blocked"
        />
      )}

      <div className="relative flex shrink-0 items-center gap-0.5">
        {/* Claiming is one click; handing work to somebody else needs the separate
            grant, and for whoever has it the same button opens the roster. With
            neither, the circle is just a label saying whose task this is. */}
        <button
          type="button"
          disabled={!mayClaim && !mayAssign}
          onClick={() => (mayAssign ? setPicking((open) => !open) : void toggleClaim(task.id))}
          aria-label={
            assignee
              ? `Assigned to ${assignee.displayName}`
              : mayAssign
                ? 'Assign this task'
                : 'Assign this task to me'
          }
          title={
            assignee
              ? `Assigned to ${assignee.displayName}`
              : mayAssign
                ? 'Assign to someone'
                : 'Take this task'
          }
          className={`grid size-7 shrink-0 place-items-center rounded-full border text-[9px] font-semibold transition-colors enabled:cursor-pointer ${
            assignee
              ? isMine
                ? 'border-accent bg-accent-soft text-ink'
                : 'border-line-strong bg-elevated text-ink-soft'
              : 'border-line border-dashed text-ink-faint enabled:hover:border-line-strong enabled:hover:text-ink'
          }`}
        >
          {assignee ? (
            initials(assignee.displayName)
          ) : (
            <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
              <path d="M8 2.5a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5ZM3.5 12.2c0-1.78 2.02-2.7 4.5-2.7s4.5.92 4.5 2.7v.55a.75.75 0 0 1-.75.75H4.25a.75.75 0 0 1-.75-.75v-.55Z" />
            </svg>
          )}
        </button>

        {picking && (
          <AssignMenu
            members={members}
            currentUserId={user?.id ?? null}
            assigneeId={task.assigneeId}
            onPick={(assigneeId) => {
              setPicking(false);
              void updateTask(task.id, { assigneeId });
            }}
            onClose={() => setPicking(false)}
          />
        )}

        <button
          type="button"
          disabled={!mayClaim}
          onClick={() => void toggleDone(task.id)}
          aria-pressed={done}
          aria-label={done ? 'Reopen this task' : 'Mark this task done'}
          title={mayClaim ? (done ? 'Reopen' : 'Done') : done ? 'Finished' : 'Not finished'}
          className={`grid size-7 shrink-0 place-items-center rounded-full border transition-colors enabled:cursor-pointer ${
            done
              ? 'border-done bg-done text-canvas'
              : 'border-line text-ink-faint enabled:hover:border-done enabled:hover:text-done'
          }`}
        >
          <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
            <path d="M13.5 4.2a.9.9 0 0 1 0 1.28l-6.1 6.1a.9.9 0 0 1-1.28 0L2.9 8.36a.9.9 0 0 1 1.28-1.27l2.58 2.58 5.46-5.47a.9.9 0 0 1 1.28 0Z" />
          </svg>
        </button>
      </div>
    </article>
  );
}

/** The admin's one-click roster: take it, hand it to somebody, or clear it. */
function AssignMenu({
  members,
  currentUserId,
  assigneeId,
  onPick,
  onClose,
}: {
  members: Array<{ id: string; displayName: string }>;
  currentUserId: string | null;
  assigneeId: string | null;
  onPick: (assigneeId: string | null) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!ref.current?.contains(event.target as Node)) onClose();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const me = members.find((m) => m.id === currentUserId);
  const others = members.filter((m) => m.id !== currentUserId);

  return (
    <div
      ref={ref}
      className="animate-rise absolute top-8 right-0 z-10 flex max-h-64 w-44 flex-col overflow-y-auto rounded-lg border border-line-strong bg-elevated p-1 shadow-xl shadow-black/40"
    >
      {me && (
        <MenuRow label="Me" active={assigneeId === me.id} onClick={() => onPick(me.id)} />
      )}
      {others.map((member) => (
        <MenuRow
          key={member.id}
          label={member.displayName}
          active={assigneeId === member.id}
          onClick={() => onPick(member.id)}
        />
      ))}
      {assigneeId && (
        <>
          <span className="my-1 h-px bg-line" />
          <MenuRow label="Nobody" active={false} onClick={() => onPick(null)} />
        </>
      )}
    </div>
  );
}

function MenuRow({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-hover ${
        active ? 'text-ink' : 'text-ink-soft'
      }`}
    >
      <span
        className={`size-1.5 shrink-0 rounded-full ${active ? 'bg-accent' : 'bg-transparent'}`}
      />
      <span className="truncate">{label}</span>
    </button>
  );
}
